import React from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import VerifiedBadge from "@/components/VerifiedBadge";
import { TrashIcon } from "@phosphor-icons/react";

function fmtTimeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  if (sec < 86400 * 7) return `${Math.floor(sec / 86400)}d`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Post card — used in the Following Feed and on a seller's public profile.
 *
 * Layout: avatar/handle row → text → image grid → attached-listing card.
 * No likes/comments yet (v1 keeps it simple by user choice).
 */
export default function PostCard({ post, currentUserId, onDeleted, compact = false }) {
  const seller = post.seller || {};
  const listing = post.listing;
  const isOwner = currentUserId && currentUserId === post.seller_id;
  const handle = seller.username ? `@${seller.username}` : seller.name || "Seller";
  const profileLink = `/seller/${post.seller_id}`;

  const remove = async () => {
    if (!window.confirm("Delete this post? This can't be undone.")) return;
    try {
      await api.delete(`/posts/${post.id}`);
      toast.success("Post deleted");
      onDeleted?.(post.id);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const imageGridClass =
    post.images?.length === 1
      ? "grid-cols-1"
      : post.images?.length === 2
      ? "grid-cols-2"
      : post.images?.length === 3
      ? "grid-cols-3"
      : "grid-cols-2";

  return (
    <article
      className="bg-white border border-zinc-200 rounded-2xl overflow-hidden"
      data-testid={`post-card-${post.id}`}
    >
      <header className="px-4 pt-3 pb-2 flex items-center justify-between gap-2">
        <Link
          to={profileLink}
          className="flex items-center gap-2.5 min-w-0 group"
          data-testid={`post-author-${post.id}`}
        >
          <div className="h-9 w-9 rounded-full bg-zinc-200 overflow-hidden flex-shrink-0 ring-2 ring-white shadow-sm">
            {seller.picture ? (
              <img src={seller.picture} alt={handle} className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-zinc-500 font-bold">
                {(seller.username || seller.name || "?")[0].toUpperCase()}
              </div>
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1 text-sm font-bold truncate group-hover:text-[#1D78AB]">
              {handle}
              {seller.verification_status === "approved" && (
                <VerifiedBadge size={12} />
              )}
            </div>
            <div className="text-[11px] text-zinc-500 font-mono">
              {fmtTimeAgo(post.created_at)}
            </div>
          </div>
        </Link>
        {isOwner && (
          <Button
            variant="ghost"
            size="icon"
            onClick={remove}
            className="h-7 w-7 text-zinc-400 hover:text-red-600"
            data-testid={`post-delete-${post.id}`}
            title="Delete post"
          >
            <TrashIcon size={14} />
          </Button>
        )}
      </header>

      {post.text ? (
        <div className="px-4 pb-3 text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">
          {post.text}
        </div>
      ) : null}

      {post.images && post.images.length > 0 && (
        <div className={`grid gap-0.5 ${imageGridClass}`}>
          {post.images.slice(0, 4).map((src, i) => (
            <a
              key={i}
              href={src}
              target="_blank"
              rel="noreferrer"
              className="aspect-square bg-zinc-100 overflow-hidden"
            >
              <img
                src={src}
                alt={`post-${i}`}
                className="h-full w-full object-cover hover:scale-105 transition"
                loading="lazy"
              />
            </a>
          ))}
        </div>
      )}

      {listing && !compact && (
        <Link
          to={`/listing/${listing.id}`}
          className="m-3 mt-3 flex items-center gap-3 p-2.5 bg-zinc-50 hover:bg-zinc-100 rounded-xl border border-zinc-200 transition"
          data-testid={`post-listing-${post.id}`}
        >
          <div className="h-14 w-14 bg-zinc-200 rounded-lg overflow-hidden flex-shrink-0">
            {listing.images?.[0] ? (
              <img
                src={listing.images[0]}
                alt={listing.title}
                className="h-full w-full object-cover"
              />
            ) : null}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span
                className={`px-1.5 py-0.5 text-[9px] font-bold tracking-widest uppercase rounded ${
                  listing.type === "auction"
                    ? "bg-zinc-900 text-white"
                    : "bg-[#1D78AB] text-white"
                }`}
              >
                {listing.type === "auction" ? "AUCTION" : "BUY NOW"}
              </span>
              <span className="text-[11px] text-zinc-500 font-mono uppercase">
                {listing.status}
              </span>
            </div>
            <div className="font-bold text-sm line-clamp-1 text-zinc-800">
              {listing.title}
            </div>
            <div className="font-mono text-[#1D78AB] font-bold text-sm">
              ${(listing.current_bid || listing.price || 0).toFixed(2)}
            </div>
          </div>
        </Link>
      )}
    </article>
  );
}
